'use client';
import { useEffect, useRef } from 'react';
import { jointColor, readSkeletonPalette, readStagePalette, type SkeletonPalette, type StagePalette } from '@/lib/theme';
import { PoseData, SKELETON_CONNECTIONS } from '@/lib/types';

interface Props {
  data: PoseData | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  cameraOn: boolean;
}

// Unified skeleton palette — reads CSS variables (theme-aware)
const MAJOR_JOINTS = new Set([5, 6, 11, 12, 13, 14]);
const FACE_JOINTS  = new Set([0, 1, 2, 3, 4]);

// Minimum keypoint confidence to render a joint/bone.
// Set low enough that YOLO's output on 640×480 frames still passes.
const KP_CONF_THRESHOLD = 0.15;

function hexToRgb(hex: string): [number, number, number] {
  return [parseInt(hex.slice(1,3),16), parseInt(hex.slice(3,5),16), parseInt(hex.slice(5,7),16)];
}

// ─────────────────────────────────────────────────────────────────────────────

export function PoseCanvas({ data, videoRef, cameraOn }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dataRef   = useRef<PoseData | null>(null);
  const cameraOnRef = useRef(cameraOn);

  // Server-camera frame cache
  const imgCache    = useRef<HTMLImageElement | null>(null);

  // SAM2 mask cache
  const maskImgRef  = useRef<HTMLImageElement | null>(null);
  const lastMaskB64 = useRef<string | null>(null);

  // Persistent offscreen canvases for hologram rendering (created once)
  const offMask = useRef<OffscreenCanvas | null>(null);
  const offHolo = useRef<OffscreenCanvas | null>(null);
  const offGlow = useRef<OffscreenCanvas | null>(null);

  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { cameraOnRef.current = cameraOn; }, [cameraOn]);

  // Decode server camera frame
  useEffect(() => {
    if (!data?.frame) { imgCache.current = null; return; }
    const img = new Image();
    img.onload = () => { imgCache.current = img; };
    img.src = `data:image/jpeg;base64,${data.frame}`;
  }, [data?.frame]);

  // Decode SAM2 mask PNG
  useEffect(() => {
    const m = data?.mask;
    if (!m || m === lastMaskB64.current) return;
    lastMaskB64.current = m;
    const img = new Image();
    img.onload = () => { maskImgRef.current = img; };
    img.src = `data:image/png;base64,${m}`;
  }, [data?.mask]);

  // Create offscreen canvases once
  useEffect(() => {
    offMask.current = new OffscreenCanvas(960, 720);
    offHolo.current = new OffscreenCanvas(960, 720);
    offGlow.current = new OffscreenCanvas(960, 720);
  }, []);

  // RAF render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rafId: number;
    const t0 = performance.now();

    const render = (now: number) => {
      const t     = (now - t0) / 1000;
      const d     = dataRef.current;
      const w     = canvas.width;
      const h     = canvas.height;
      const video = videoRef.current;

      const hasBrowserCam  = cameraOnRef.current &&
                             !!(video && video.readyState >= 2 && !video.paused);
      const hasServerFrame = !!(d?.frame && imgCache.current);
      const hasMask        = !!(maskImgRef.current &&
                                offMask.current && offHolo.current && offGlow.current);

      ctx.clearRect(0, 0, w, h);

      // ── 1. Background ─────────────────────────────────────────────────────
      if (hasBrowserCam) {
        // The <video> element is visible behind this canvas.
        // Only add a dim overlay when the hologram mask is active so the
        // glowing silhouette stands out; otherwise leave the canvas transparent.
        if (hasMask) {
          ctx.fillStyle = 'rgba(0,0,0,0.45)';
          ctx.fillRect(0, 0, w, h);
        }
      } else if (hasServerFrame) {
        ctx.globalAlpha = hasMask ? 0.45 : 0.92;
        ctx.drawImage(imgCache.current!, 0, 0, w, h);
        ctx.globalAlpha = 1;
      } else if (d?.keypoints?.length || d?.demo) {
        drawDemoBackground(ctx, w, h, t, readStagePalette());
      }

      // ── 2. SAM2 hologram effect ───────────────────────────────────────────
      if (hasMask && (hasBrowserCam || hasServerFrame)) {
        drawHologram(
          ctx, maskImgRef.current!, w, h, t,
          offMask.current!, offHolo.current!, offGlow.current!,
        );
      }

      // ── 3. Skeleton ───────────────────────────────────────────────────────
      if (d?.keypoints?.length) {
        const palette = readSkeletonPalette();
        drawSkeleton(ctx, d.keypoints, w, h, t, palette);
      }

      rafId = requestAnimationFrame(render);
    };

    rafId = requestAnimationFrame(render);
    return () => cancelAnimationFrame(rafId);
  }, [videoRef]);

  return (
    <canvas
      ref={canvasRef}
      width={960}
      height={720}
      className="absolute inset-0 w-full h-full"
    />
  );
}

// ─── Hologram body effect ─────────────────────────────────────────────────────

function drawHologram(
  ctx: CanvasRenderingContext2D,
  maskImg: HTMLImageElement,
  w: number, h: number, t: number,
  offMask: OffscreenCanvas,
  offHolo: OffscreenCanvas,
  offGlow: OffscreenCanvas,
) {
  // 1. Upscale mask to canvas size
  const mCtx = offMask.getContext('2d')!;
  mCtx.clearRect(0, 0, w, h);
  mCtx.drawImage(maskImg, 0, 0, w, h);

  // 2. Hologram body: animated scan bands + gradient base
  const hCtx = offHolo.getContext('2d')!;
  hCtx.clearRect(0, 0, w, h);

  const bodyGrad = hCtx.createLinearGradient(0, 0, 0, h);
  bodyGrad.addColorStop(0,    'rgba(109, 181, 168, 0.16)');
  bodyGrad.addColorStop(0.45, 'rgba(109, 181, 168, 0.08)');
  bodyGrad.addColorStop(1,    'rgba(109, 181, 168, 0.14)');
  hCtx.fillStyle = bodyGrad;
  hCtx.fillRect(0, 0, w, h);

  const bandH   = 55;
  const bandGap = 90;
  const scanOff = (t * 70) % (bandH + bandGap);
  for (let y = -(bandH + bandGap); y < h + bandH; y += bandH + bandGap) {
    const gy = y + scanOff;
    const sg = hCtx.createLinearGradient(0, gy, 0, gy + bandH);
    sg.addColorStop(0,    'rgba(109, 181, 168, 0)');
    sg.addColorStop(0.35, 'rgba(109, 181, 168, 0.18)');
    sg.addColorStop(0.65, 'rgba(109, 181, 168, 0.12)');
    sg.addColorStop(1,    'rgba(109, 181, 168, 0)');
    hCtx.fillStyle = sg;
    hCtx.fillRect(0, gy, w, bandH);
  }

  hCtx.fillStyle = 'rgba(0, 0, 0, 0.18)';
  for (let y = 0; y < h; y += 4) hCtx.fillRect(0, y + 2, w, 2);

  hCtx.globalCompositeOperation = 'destination-in';
  hCtx.drawImage(offMask, 0, 0);
  hCtx.globalCompositeOperation = 'source-over';

  ctx.drawImage(offHolo, 0, 0);

  // 3. Glowing outline
  const gCtx = offGlow.getContext('2d')!;
  gCtx.clearRect(0, 0, w, h);

  const pulse = 0.65 + 0.35 * Math.sin(t * 1.8);
  gCtx.drawImage(offMask, 0, 0);
  gCtx.globalCompositeOperation = 'source-in';
  gCtx.fillStyle = `rgba(109, 181, 168, ${0.45 * pulse})`;
  gCtx.fillRect(0, 0, w, h);
  gCtx.globalCompositeOperation = 'source-over';

  ctx.save();
  ctx.filter = 'blur(20px)';
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = 0.45;
  ctx.drawImage(offGlow, 0, 0);

  ctx.filter = 'blur(5px)';
  ctx.globalAlpha = 0.70;
  ctx.drawImage(offGlow, 0, 0);
  ctx.restore();
}

// ─── Demo background ──────────────────────────────────────────────────────────

function drawDemoBackground(
  ctx: CanvasRenderingContext2D,
  w: number, h: number, t: number,
  stage: StagePalette,
) {
  const { secondary: dim } = readSkeletonPalette();
  const bg = ctx.createRadialGradient(w * .5, h * .38, 0, w * .5, h * .5, Math.max(w, h) * .85);
  bg.addColorStop(0, stage.top);
  bg.addColorStop(.6, stage.mid);
  bg.addColorStop(1, stage.bottom);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  const horizon = h * .76;
  ctx.save();
  ctx.lineCap = 'butt';
  for (let i = 0; i <= 10; i++) {
    const f = (i / 10) ** 1.6, y = horizon + (h - horizon) * f;
    ctx.globalAlpha = f * .18;
    ctx.strokeStyle = stage.grid;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
  }
  for (let i = -10; i <= 10; i++) {
    const endX = w * .5 + (i / 10) * (w * .55);
    ctx.globalAlpha = (1 - Math.abs(i) / 11) * .12;
    ctx.strokeStyle = dim;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(w * .5, horizon); ctx.lineTo(endX, h); ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  const sy = (t * 80) % h;
  const sg = ctx.createLinearGradient(0, sy - 30, 0, sy + 30);
  sg.addColorStop(0, 'transparent');
  sg.addColorStop(.5, stage.scan);
  sg.addColorStop(1, 'transparent');
  ctx.fillStyle = sg;
  ctx.fillRect(0, sy - 30, w, 60);

  const vg = ctx.createRadialGradient(w / 2, h / 2, h * .25, w / 2, h / 2, Math.max(w, h) * .75);
  vg.addColorStop(0, 'transparent');
  vg.addColorStop(1, stage.vignette);
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, w, h);
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function drawSkeleton(
  ctx: CanvasRenderingContext2D,
  keypoints: [number, number, number][],
  w: number, h: number, t: number,
  palette: SkeletonPalette,
) {
  if (!keypoints || keypoints.length < 17) return;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Pass 1: soft halo (very subtle)
  ctx.globalCompositeOperation = 'lighter';
  for (const [a, b] of SKELETON_CONNECTIONS) {
    const kpA = keypoints[a], kpB = keypoints[b];
    if (!kpA || !kpB || kpA[2] < KP_CONF_THRESHOLD || kpB[2] < KP_CONF_THRESHOLD) continue;
    const conf = (kpA[2] + kpB[2]) / 2;
    const x1 = kpA[0] * w, y1 = kpA[1] * h, x2 = kpB[0] * w, y2 = kpB[1] * h;
    ctx.strokeStyle = boneGrad(ctx, x1, y1, x2, y2, a, b, palette);
    ctx.lineWidth = 5;
    ctx.globalAlpha = 0.06 * conf;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  }

  // Pass 2: crisp bone lines
  ctx.globalCompositeOperation = 'source-over';
  for (const [a, b] of SKELETON_CONNECTIONS) {
    const kpA = keypoints[a], kpB = keypoints[b];
    if (!kpA || !kpB || kpA[2] < KP_CONF_THRESHOLD || kpB[2] < KP_CONF_THRESHOLD) continue;
    const conf = (kpA[2] + kpB[2]) / 2;
    const x1 = kpA[0] * w, y1 = kpA[1] * h, x2 = kpB[0] * w, y2 = kpB[1] * h;
    ctx.strokeStyle = boneGrad(ctx, x1, y1, x2, y2, a, b, palette);
    ctx.lineWidth = 1.25;
    ctx.globalAlpha = Math.min(conf * 0.95, 0.92);
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Pass 3: joints — minimal dots + thin rings on major joints only
  for (let i = 0; i < keypoints.length; i++) {
    const kp = keypoints[i];
    if (!kp || kp[2] < KP_CONF_THRESHOLD) continue;
    const x = kp[0] * w, y = kp[1] * h, conf = kp[2];
    const color = jointColor(i, palette);
    const [r, g, b] = hexToRgb(color);

    if (FACE_JOINTS.has(i)) {
      ctx.beginPath(); ctx.arc(x, y, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r},${g},${b},${conf * 0.45})`;
      ctx.fill();
      continue;
    }

    const isMajor = MAJOR_JOINTS.has(i);
    const dotR = isMajor ? 2.2 : 1.6;

    if (isMajor) {
      const pulse = 1 + 0.03 * Math.sin(t * 2.5 + i * 0.6);
      const ringR = 5 * pulse;
      ctx.beginPath(); ctx.arc(x, y, ringR, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${r},${g},${b},${0.22 * conf})`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    ctx.beginPath(); ctx.arc(x, y, dotR, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${r},${g},${b},${Math.min(conf * 0.9, 0.95)})`;
    ctx.fill();

    ctx.beginPath(); ctx.arc(x, y, dotR + 0.8, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(255,255,255,${0.25 * conf})`;
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }

  ctx.restore();
}

function boneGrad(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number, x2: number, y2: number,
  a: number, b: number,
  palette: SkeletonPalette,
): CanvasGradient {
  const g = ctx.createLinearGradient(x1, y1, x2, y2);
  g.addColorStop(0, jointColor(a, palette));
  g.addColorStop(1, jointColor(b, palette));
  return g;
}
